import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const { stores, user, login, admins, isLoading: isStoreLoading } = useStore();
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isLoggingInProcess, setIsLoggingInProcess] = useState(false); // [Fix] 대시보드 플래시백 방지 플래그

    // 이미 로그인 상태라면 대시보드로 자동 리다이렉트 (단, 직접 로그인 중인 상태라면 랜딩페이지 흐름 유지를 위해 제외)
    React.useEffect(() => {
        if (!isStoreLoading && user && !isLoggingInProcess) {
            navigate('/dashboard');
        }
    }, [user, isStoreLoading, isLoggingInProcess]);

    const handleSubmit = (e, isForce = false) => {
        if (e) e.preventDefault();

        // 유효성 검사
        if (!userId || !password) {
            setMessage({ type: 'error', text: '아이디와 비밀번호를 모두 입력해 주세요.' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });

        // 가상의 로그인 API 통신 (0.5초 대기)
        setTimeout(async () => {
            setIsLoading(false);

            const trimmedId = userId.trim();
            const lowerId = trimmedId.toLowerCase();

            // 1. 관리자 로그인 확인 (서버에서 가져온 admins 목록 활용)
            const adminAccount = admins.find(a => a.id.toLowerCase() === lowerId && a.password === password);
            if (adminAccount) {
                // admin1, admin2, admin3 계정 PC(1260px 이상) 접속 제한
                const restrictedAdmins = ['admin1', 'admin2', 'admin3'];
                if (restrictedAdmins.includes(adminAccount.id.toLowerCase()) && window.innerWidth > 768) {
                    const adminNum = adminAccount.id.slice(-1);
                    setMessage({ 
                        type: 'error', 
                        text: `관리자${adminNum} 계정은 모바일 기기에서만 접근 가능합니다.` 
                    });
                    return;
                }

                // admin1, admin2, admin3 계정 동시 접속 방지 (선접속자 우선)
                if (restrictedAdmins.includes(adminAccount.id.toLowerCase())) {
                    try {
                        const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                            ? 'http://localhost:3001/api' 
                            : `${window.location.origin}/api`;
                            
                        const res = await fetch(`${baseUrl}/check-and-login`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ userId: adminAccount.id, force: isForce })
                        });
                        
                        if (res.status === 409) {
                            const data = await res.json();
                            setMessage({
                                type: 'error',
                                text: data.message || '현재 다른 사용자가 접속 중인 계정입니다.'
                            });
                            return;
                        } else if (!res.ok) {
                            throw new Error('Session check failed');
                        }
                    } catch (err) {
                        console.error('Session check error:', err);
                        // 에러가 나더라도 일단 로그인을 막거나, 예외적으로 풀어줄 수 있음 (여기서는 안전하게 계속 진행 허용)
                    }
                }

                const adminUser = { 
                    role: 'admin', 
                    id: adminAccount.id, 
                    name: adminAccount.name
                };
                setIsLoggingInProcess(true); // 플래시백 방지 플래그 On
                login(adminUser);
                
                // 로그인 로그 기록 (admin1-4)
                if (['admin1', 'admin2', 'admin3', 'admin4'].includes(adminAccount.id)) {
                    fetch(`${window.location.origin}/api/access-logs`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId: adminAccount.id,
                            action: 'login',
                            details: '관리자 로그인',
                            timestamp: new Date().toISOString()
                        })
                    }).catch(err => console.error('Logging failed:', err));
                }

                const welcomeMsg = `관리자 계정으로 입장합니다.`;
                setMessage({ type: 'success', text: welcomeMsg });
                setTimeout(() => {
                    navigate('/landing');
                }, 1000);
                return;
            }

            // 2. 매장 매니저 로그인 확인
            const store = stores.find(s => s.managerId.toLowerCase() === lowerId && s.password === password);

            if (store) {
                // 매니저는 모바일(768px 이하)에서만 로그인 가능
                if (window.innerWidth > 768) {
                    setMessage({
                        type: 'error',
                        text: '매니저 계정은 모바일 기기에서만 접근 가능합니다. 📱'
                    });
                    return;
                }
                const managerUser = { role: 'manager', id: trimmedId, storeId: store.id, name: store.name, permission: 'full' };
                setIsLoggingInProcess(true); // 플래시백 방지 플래그 On
                login(managerUser);
                setMessage({ type: 'success', text: `${store.name} 매니저님, 환영합니다!` });
                setTimeout(() => {
                    navigate('/landing');
                }, 1000);
            } else {
                setMessage({ type: 'error', text: '아이디 또는 비밀번호가 일치하지 않습니다.' });
            }
        }, 500);
    };

    return (
        <div className="login-container">
            <div className="glass-card">
                <div className="login-header">
                    <h1>BASEBALLFOOD <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>v3.5.7</span></h1>
                    <p>내 손안의 야구장, 로그인을 환영합니다</p>
                </div>

                {message.text && (
                    <div className={`alert-message ${message.type}`}>
                        {message.text}
                        {message.text.includes('접속 중인 계정') && (
                            <button 
                                onClick={(e) => handleSubmit(e, true)}
                                style={{
                                    marginTop: '10px',
                                    padding: '6px 12px',
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'white',
                                    fontSize: '0.8rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'block',
                                    width: '100%'
                                }}
                            >
                                현재 세션 종료 후 강제 접속하기
                            </button>
                        )}
                    </div>
                )}

                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label htmlFor="userId">아이디</label>
                        <input
                            type="text"
                            id="userId"
                            placeholder="아이디를 입력하세요"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="password">비밀번호</label>
                        <input
                            type="password"
                            id="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    <button type="submit" className={`login-button ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
                        {isLoading ? '연결 중...' : '로그인'}
                    </button>
                </form>

                <div className="login-footer" style={{ 
                    marginTop: '20px',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    textAlign: 'center'
                }}>
                    <div style={{ opacity: 0.8, fontSize: '14px', color: '#ffffff', fontWeight: 'bold' }}>
                        v3.5.7.2
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
